import { useState, useEffect } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import {
  RiMenuLine, RiCloseLine,
  RiCalendarCheckLine, RiUserLine, RiSettings3Line,
  RiLogoutBoxLine, RiDashboardLine,
} from 'react-icons/ri'

const links = [
  { to: '/services', label: 'Services' },
  { to: '/pricing',  label: 'Pricing' },
  { to: '/our-team', label: 'Our Team' },
  { to: '/gallery',  label: 'Gallery' },
  { to: '/about',    label: 'About' },
  { to: '/contact',  label: 'Contact' },
]

export default function Navbar() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [scrolled, setScrolled] = useState(false)
  const [open,     setOpen]     = useState(false)
  const [dropDown, setDropDown] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const handleLogout = async () => {
    setDropDown(false)
    await logout()
    navigate('/')
  }

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-salon-dark/95 backdrop-blur-md shadow-salon-md py-2'
          : 'bg-gradient-to-b from-salon-dark/80 via-salon-dark/40 to-transparent py-3'
      }`}
    >
      <div className="container-salon flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center group shrink-0" onClick={() => setOpen(false)} aria-label="Salon Vora — Home">
          <img
            src="/brand/logo.jpg"
            alt="Salon Vora"
            width="220"
            height="80"
            className={`object-contain transition-all duration-300 rounded-md ${
              scrolled ? 'h-10 sm:h-11' : 'h-12 sm:h-14'
            }`}
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full font-body font-medium text-sm transition-all duration-200 ${
                  isActive
                    ? 'text-salon-secondary bg-white/10'
                    : 'text-white/85 hover:text-salon-secondary hover:bg-white/10'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <Link to="/book" className="btn-gold text-sm px-5 py-2.5 shadow-gold">
            <RiCalendarCheckLine /> Book Now
          </Link>

          {user ? (
            <div className="relative">
              <button
                onClick={() => setDropDown(!dropDown)}
                className="flex items-center gap-2 px-3 py-2 rounded-full
                           bg-salon-primary/10 hover:bg-salon-primary/20 transition-all"
              >
                <div className="w-7 h-7 rounded-full bg-salon-primary flex items-center justify-center text-white text-xs font-bold">
                  {user.name?.charAt(0)}
                </div>
                <span className="text-sm font-medium font-body text-white">
                  {user.name?.split(' ')[0]}
                </span>
              </button>

              {dropDown && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setDropDown(false)} />
                  <div className="absolute right-0 top-12 w-52 bg-white rounded-2xl shadow-salon-lg border border-salon-cream-dark
                                  z-20 py-2 animate-fade-up">
                    {user.role === 'admin' && (
                      <Link to="/admin" onClick={() => setDropDown(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm font-body text-salon-dark
                                   hover:bg-salon-cream transition-colors">
                        <RiSettings3Line className="text-salon-primary" /> Admin Panel
                      </Link>
                    )}
                    <Link to="/dashboard" onClick={() => setDropDown(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm font-body text-salon-dark
                                 hover:bg-salon-cream transition-colors">
                      <RiDashboardLine className="text-salon-primary" /> Dashboard
                    </Link>
                    <Link to="/dashboard/appointments" onClick={() => setDropDown(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm font-body text-salon-dark
                                 hover:bg-salon-cream transition-colors">
                      <RiCalendarCheckLine className="text-salon-primary" /> My Bookings
                    </Link>
                    <Link to="/dashboard/profile" onClick={() => setDropDown(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm font-body text-salon-dark
                                 hover:bg-salon-cream transition-colors">
                      <RiUserLine className="text-salon-primary" /> Profile
                    </Link>
                    <div className="border-t border-salon-cream-dark my-1" />
                    <button onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-body text-red-500
                                 hover:bg-red-50 transition-colors">
                      <RiLogoutBoxLine /> Logout
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <Link to="/login"
              className="text-sm font-semibold font-body px-5 py-2.5 rounded-full border-2 transition-all duration-200
                         border-white/50 text-white hover:border-salon-secondary hover:text-salon-secondary">
              Sign In
            </Link>
          )}
        </div>

        {/* Mobile menu toggle */}
        <button
          className="lg:hidden p-2 rounded-xl transition-colors text-white hover:text-salon-secondary"
          aria-label={open ? 'Close menu' : 'Open menu'}
          onClick={() => setOpen(!open)}
        >
          {open ? <RiCloseLine className="text-2xl" /> : <RiMenuLine className="text-2xl" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-white border-t border-salon-cream-dark shadow-salon-lg animate-fade-up">
          <div className="container-salon py-6 space-y-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl font-body font-medium text-sm transition-colors ${
                    isActive ? 'bg-salon-primary text-white' : 'text-salon-dark hover:bg-salon-cream'
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="pt-4 flex flex-col gap-3">
              <Link to="/book" onClick={() => setOpen(false)} className="btn-gold justify-center">
                <RiCalendarCheckLine /> Book Now
              </Link>
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setOpen(false)} className="btn-secondary justify-center text-sm">
                    Dashboard
                  </Link>
                  <button onClick={() => { setOpen(false); handleLogout() }}
                    className="text-red-500 text-sm font-semibold font-body py-2">
                    Logout
                  </button>
                </>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} className="btn-secondary justify-center text-sm">
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
